import { useState } from 'react';
import { ReactComponent as InfoIcon } from '@assets/icons/info-light-circle.svg';
import config from 'config';
import parse from 'html-react-parser';
import Image from 'next/image';

import {
  Box,
  Button,
  Flex,
  Stack,
  Text,
  useBoolean,
  useTheme
} from '@chakra-ui/react';

import { ProviderInterface } from 'types';

import { Loader } from 'ui';
import { ProviderInfoModal } from './components';

const {
  booking: { defaultProvider, DefaultProviderAvatar }
} = config;

interface ProviderProps {
  provider: ProviderInterface;
  onClickProvider: () => void;
  isChecked: boolean;
  isDisabled: boolean;
}

const ProviderItem = ({
  provider,
  onClickProvider,
  isChecked,
  isDisabled
}: ProviderProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: {
            serviceList: { serviceItem },
            providerList: { providerItem }
          }
        }
      }
    }
  } = useTheme();

  const [isProviderModalOpen, setIsProviderModalOpen] =
    useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);
  const [loading, setLoading] = useBoolean(true);

  const isDefaultProvider = provider.id === defaultProvider.id;
  const providerName = isDefaultProvider ? defaultProvider.name : provider.name;
  const providerDescription = isDefaultProvider
    ? defaultProvider.description
    : provider.public_title;

  const handleOpenModal = () => {
    setIsProviderModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsProviderModalOpen(false);
  };

  return (
    <>
      <Flex
        p='20px'
        alignItems='center'
        gap='20px'
        w={{ base: '100%', md: 'calc(50% - 2px)' }}
        bg={providerItem.bg}
        opacity={isDisabled ? 0.5 : 1}
        cursor='pointer'
        onClick={onClickProvider}
        pointerEvents={isDisabled ? 'none' : 'auto'}
      >
        <Box w='25px' h='25px' minW='25px' bg='transparent'>
          <Box
            w='100%'
            h='100%'
            borderWidth={isChecked ? '8px' : '1px'}
            borderStyle='solid'
            borderColor={providerItem.borderColor}
            borderRadius='50%'
          />
        </Box>

        <Box
          w='65px'
          h='65px'
          borderRadius='50%'
          overflow='hidden'
          position='relative'
          flexShrink={0}
        >
          {provider?.public_photo_url?.includes('http') && !imageError ? (
            <>
              {loading && <Loader absolute />}
              <Image
                loading='lazy'
                sizes='(max-width: 450px) 40px, 65px'
                src={provider.public_photo_url}
                alt={provider.name}
                width={65}
                height={65}
                onError={() => setImageError(true)}
                onLoad={setLoading.off}
              />
            </>
          ) : (
            <DefaultProviderAvatar
              width={65}
              height={65}
              fill={providerItem.iconFill}
              style={{ opacity: 0.5 }}
            />
          )}
        </Box>

        <Stack gap='2px' flex='1'>
          <Flex justifyContent='space-between' alignItems='center'>
            <Text fontSize='16px' textTransform='uppercase'>
              {providerName}
            </Text>
            <Button
              display={isDefaultProvider ? 'none' : 'flex'}
              flexShrink={0}
              variant='text'
              onClick={handleOpenModal}
              _hover={{
                '& p, & svg': {
                  color: serviceItem.hoverTextColor,
                  fill: serviceItem.hoverIconColor
                }
              }}
            >
              <InfoIcon width={18} height={18} fill={serviceItem.iconColor} />
            </Button>
          </Flex>

          <Text
            fontSize='14px'
            color={providerItem.descriptionColor}
            lineHeight='120%'
            noOfLines={3}
          >
            {providerDescription ? parse(providerDescription) : ''}
          </Text>
        </Stack>
      </Flex>

      <ProviderInfoModal
        isOpen={isProviderModalOpen}
        onClose={handleCloseModal}
        provider={provider}
      />
    </>
  );
};

export default ProviderItem;
